import type { TypedDocumentNode } from "urql";
import { gql } from "urql";
import { useLocale } from "@acto/web-common/locales";
import { useComparisonModeContext, useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import {
  Kpi as GqlKpi,
  type KpiComparisonMode,
  ProductOrderBy,
  ProductRowScope,
  ProductTrendFilter,
} from "@acto/gql/graphql";
import { Kpi } from "@acto/taskpane/core/domain";
import { toGqlKpiComparisonMode } from "@acto/taskpane/core/data-access/customers/kpiTransformers";
import { useNumberFormatter } from "@acto/taskpane/core/utils/hooks/useNumberFormatter.tsx";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import {
  type ProductGroupNode,
  toOrderIntakeValueItem,
  toOrderKpi,
  toProductDeltaItem,
  toProductValueItem,
} from "@acto/taskpane/field-sales-2/data-access/productTrendItem";
import type { ProductTrendItem } from "@acto/taskpane/field-sales-2/components/primitives/ProductTrendList";

const DEFAULT_PAGE_SIZE = 5;

export interface ProductGroups {
  isPending: boolean;
  isEmpty: boolean;
  error: Error | undefined;
  items: ProductTrendItem[];
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  /** True when the current result set is filtered by a search term. */
  isSearchActive: boolean;
}

interface ProductGroupsQueryData {
  productGroups: {
    edges: { node: ProductGroupNode }[];
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
  };
}

interface ProductGroupsQueryVariables {
  clientId?: string | null;
  kpis: GqlKpi[];
  parents?: readonly string[] | null;
  first: number;
  after?: string | null;
  dateRange?: { startDate: string; endDate: string };
  searchTerm?: string | null;
  trends?: ProductTrendFilter[] | null;
  orderKpi?: GqlKpi | null;
  orderBy?: ProductOrderBy;
  comparisonMode?: KpiComparisonMode;
  rowScope?: ProductRowScope;
}

const ProductGroupsQuery: TypedDocumentNode<ProductGroupsQueryData, ProductGroupsQueryVariables> = gql`
  query GetProductGroups(
    $clientId: ID
    $kpis: [Kpi!]
    $parents: [ID!]
    $first: Int
    $after: String
    $dateRange: DateRangeInput
    $searchTerm: String
    $trends: [ProductTrendFilter!]
    $orderKpi: Kpi
    $orderBy: ProductOrderBy
    $comparisonMode: KpiComparisonMode
    $rowScope: ProductRowScope
  ) {
    productGroups(
      clientId: $clientId
      kpis: $kpis
      parents: $parents
      first: $first
      after: $after
      dateRange: $dateRange
      searchTerm: $searchTerm
      trends: $trends
      orderKpi: $orderKpi
      orderBy: $orderBy
      comparisonMode: $comparisonMode
      rowScope: $rowScope
    ) {
      edges {
        node {
          id
          productGroup
          externalReference
          hasChildren
          values {
            kpi
            value
            previousValue
            change {
              percentageChange
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export interface ProductGroupsOptions {
  /** Omit to scope to the caller's accessible clients */
  clientId?: string;
  parents?: readonly string[];
  dateRange?: { startDate: string; endDate: string } | null;
  searchTerm?: string;
  selectedKpi?: Kpi;
  comparisonMode?: KpiComparisonMode;
  rowScope?: ProductRowScope;
  order?: ProductOrderBy;
  trends?: ProductTrendFilter[];
  /** Values, sorts and trend-filters the rows by order intake instead of `selectedKpi`, always as period values. */
  byOrderIntake?: boolean;
  /** Pause the query, e.g. while a route param is still resolving. Defaults to true. */
  enabled?: boolean;
  pageSize?: number;
}

export function useProductGroups({
  clientId,
  parents,
  dateRange,
  searchTerm,
  selectedKpi = Kpi.REVENUE,
  comparisonMode: explicitComparisonMode,
  rowScope = ProductRowScope.CurrentPeriod,
  order = ProductOrderBy.HighestValue,
  trends,
  byOrderIntake = false,
  enabled = true,
  pageSize = DEFAULT_PAGE_SIZE,
}: ProductGroupsOptions): ProductGroups {
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const formatNumber = useNumberFormatter();
  const tenantComparisonMode = toGqlKpiComparisonMode(useComparisonModeContext());
  const comparisonMode = explicitComparisonMode ?? tenantComparisonMode;

  const trimmedSearchTerm = searchTerm?.trim() ?? "";
  const activeSearchTerm = trimmedSearchTerm.length > 0 ? trimmedSearchTerm : null;
  const activeTrends = trends && trends.length > 0 ? trends : undefined;
  const orderKpi = byOrderIntake ? GqlKpi.IncomingOrder : toOrderKpi(selectedKpi);

  const { data, isFetching, hasNextPage, fetchNextPage, error } = usePagination<
    ProductGroupsQueryData,
    ProductGroupsQueryVariables,
    ProductGroupNode
  >({
    query: ProductGroupsQuery,
    limit: pageSize,
    enabled,
    variables: {
      clientId: clientId ?? null,
      kpis: byOrderIntake ? [GqlKpi.IncomingOrder] : [GqlKpi.Revenue, GqlKpi.Profit, GqlKpi.Margin, GqlKpi.Orders],
      parents: parents ?? null,
      dateRange: dateRange ?? undefined,
      searchTerm: activeSearchTerm,
      trends: activeTrends,
      orderKpi,
      orderBy: order,
      comparisonMode,
      rowScope,
    },
    connection: (data) => data.productGroups,
  });

  const items = (data ?? []).map((node) =>
    byOrderIntake
      ? toOrderIntakeValueItem(node, locale, currency, formatNumber)
      : rowScope === ProductRowScope.EitherPeriod
        ? toProductDeltaItem(node, selectedKpi, locale, currency, formatNumber)
        : toProductValueItem(node, selectedKpi, locale, currency, formatNumber),
  );

  // First-page (re)load including stale revalidation: jumping to another group resets the list to []
  // while urql serves a lingering partial cache hit (fetching false, stale true), so gate on the
  // accumulated list. usePagination's `isPending` reads urql's raw data and stays false through it.
  const isFirstPageLoading = isFetching && items.length === 0;

  return {
    isPending: isFirstPageLoading,
    isEmpty: !isFirstPageLoading && !error && items.length === 0,
    error,
    items,
    hasMore: hasNextPage,
    isLoadingMore: isFetching && items.length > 0,
    loadMore: fetchNextPage,
    isSearchActive: activeSearchTerm != null,
  };
}
